/**
 * Motor de formulários dinâmicos por produto (Onda 4 / L5–L6, desenho AN-6).
 *
 * Espelha o contrato publicado pelo backend de oportunidades (DEV-J):
 *  - `GET/PUT /v1/oportunidades/{id}/formulario?etapa=X` → `FormularioReserva`;
 *  - `GET /v1/oportunidades/{id}/formulario/contexto-ia` → `ItemContextoIa[]`.
 *
 * O schema é DADO versionado (cadastrado pelo ADMIN), nunca código: nenhuma tela monta
 * campo fixo. O front só avalia `condition`/`requiredWhen` para mostrar/ocultar e pôr o
 * asterisco — quem decide obrigatoriedade e bloqueia envio é o servidor (`pendencias`).
 *
 * Dono: DEV-L. O DEV-K (editor do admin) acrescenta aqui só o que for do admin.
 */

/** Etapas da reserva que têm formulário (enum fechado no backend). */
export type EtapaFormulario = 'CADASTRO' | 'ESTRUTURACAO'

/** Marco em que um campo passa a ser exigido (cumulativo: ENCAMINHAMENTO inclui ENVIO). */
export type MarcoExigencia = 'ENVIO_ANALISE' | 'ENCAMINHAMENTO_PARCEIRO'

export const MARCO_EXIGENCIA_LABEL: Record<MarcoExigencia, string> = {
  ENVIO_ANALISE: 'envio para análise',
  ENCAMINHAMENTO_PARCEIRO: 'encaminhamento ao parceiro',
}

export type TipoCampoFormulario =
  | 'TEXTO'
  | 'TEXTO_LONGO'
  | 'NUMERO'
  | 'MOEDA'
  | 'PERCENTUAL'
  | 'DATA'
  | 'SIM_NAO'
  | 'OPCAO'
  | 'CPF'
  | 'CNPJ'
  | 'EMAIL'
  | 'TELEFONE'
  | 'GRUPO'

export type OperadorCondicao = 'IGUAL' | 'DIFERENTE' | 'UM_DE' | 'PREENCHIDO' | 'MAIOR_QUE'

/**
 * Condição de visibilidade (`condition`) ou de obrigatoriedade (`requiredWhen`). `campo`
 * referencia um campo ANTERIOR do topo ou um irmão do mesmo grupo. `valor` é sempre texto
 * (SIM_NAO → "true"/"false"); `UM_DE` usa `valores` (ou `valor` sozinho como lista de um).
 */
export interface CondicaoCampo {
  campo: string
  operador: OperadorCondicao
  valor?: string | null
  valores?: string[] | null
}

export interface OpcaoCampo {
  valor: string
  rotulo: string
}

/** Validação de grupo — hoje só a SOMA (ex.: participações = 100%). */
export interface ValidacaoGrupo {
  tipo: 'SOMA'
  campo: string
  valor: number
  tolerancia?: number | null
  marco?: MarcoExigencia | null
  mensagem?: string | null
}

export interface ItemFormulario {
  chave: string
  rotulo: string
  tipo: TipoCampoFormulario
  /** Texto de apoio abaixo do campo. */
  ajuda?: string | null
  /** Marco em que passa a ser exigido; ausente = opcional. */
  obrigatorioEm?: MarcoExigencia | null
  condition?: CondicaoCampo | null
  requiredWhen?: CondicaoCampo | null
  min?: number | null
  max?: number | null
  opcoes?: OpcaoCampo[] | null
  /** Entra no contexto da Defesa com IA (nunca em CPF — o publicador recusa). */
  enviarParaIa?: boolean | null
  // ── GRUPO (um nível) ──
  repeatableGroup?: boolean | null
  /** Chave do NUMERO do topo que define quantos itens o grupo tem. */
  quantidadeDe?: string | null
  minItems?: number | null
  maxItems?: number | null
  campos?: ItemFormulario[] | null
  validacoes?: ValidacaoGrupo[] | null
}

export interface SchemaFormulario {
  itens: ItemFormulario[]
}

/**
 * Valor de uma resposta como trafega no JSON: texto (TEXTO, EMAIL, CPF, DATA "AAAA-MM-DD",
 * OPCAO = `valor` da opção), número (NUMERO, MOEDA, PERCENTUAL) ou booleano (SIM_NAO).
 */
export type ValorResposta = string | number | boolean | null

/**
 * Uma resposta. Campo do topo: `grupo` vazio ("") e `indice` 0. Campo de grupo: `grupo` =
 * chave do GRUPO e `indice` = posição do item (0..n-1).
 */
export interface RespostaFormulario {
  grupo: string
  indice: number
  campo: string
  valor: ValorResposta
}

export type CodigoPendenciaFormulario =
  | 'OBRIGATORIO'
  | 'QUANTIDADE_ITENS'
  | 'MIN_ITENS'
  | 'MAX_ITENS'
  | 'SOMA'

/** O que ainda falta, calculado pelo SERVIDOR sobre a versão vinculada. */
export interface PendenciaFormulario {
  grupo?: string | null
  indice?: number | null
  campo?: string | null
  codigo: CodigoPendenciaFormulario | string
  mensagem: string
  marco: MarcoExigencia
}

/** Resposta de `GET/PUT /v1/oportunidades/{id}/formulario?etapa=`. */
export interface FormularioReserva {
  /** NULL = a reserva não tem formulário nesta etapa (produto sem formulário, reserva antiga). */
  formularioId: string | null
  /** Versão do formulário (0 = LEGADO, os 7 dados complementares da V39). */
  versao: number | null
  schema: SchemaFormulario | null
  /** A versão já está fixada na reserva (senão é a vigente, oferecida). */
  vinculada: boolean
  /** Dentro da janela de edição da etapa para quem pede. */
  editavel: boolean
  respostas: RespostaFormulario[]
  pendencias: PendenciaFormulario[]
}

/** Corpo do `PUT`. Versão divergente da vinculada → 409 `FORMULARIO_VERSAO_DIVERGENTE`. */
export interface SalvarFormularioRequest {
  formularioId: string
  respostas: RespostaFormulario[]
}

/** Item do contexto da Defesa com IA: só campos `enviarParaIa`, nunca CPF. */
export interface ItemContextoIa {
  rotulo: string
  valor: string
}

/** Grupo das respostas de campos do topo. */
export const GRUPO_TOPO = ''

/** Há formulário de fato (com ao menos um item) nesta resposta? */
export const temFormulario = (f: FormularioReserva | null | undefined): boolean =>
  !!f && !!f.formularioId && !!f.schema && (f.schema.itens?.length ?? 0) > 0
